import React, { useLayoutEffect, useMemo } from 'react';
import {
  Box,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  Button,
} from '@mui/material';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { resolveMetricDataKey } from '../../lib/reports/reportQueryConfig';

const CHART_MARGIN = { top: 12, right: 24, left: 8, bottom: 8 };

function notifyChartResize() {
  window.dispatchEvent(new Event('resize'));
}

/**
 * Right canvas — chart/table toggle, control toolbar, optional fullscreen overlay.
 */
export default function ReportVisualizationCanvas({
  rows,
  meta,
  queryConfig,
  viewMode,
  onViewModeChange,
  hasRun,
  loading,
  isSidebarCollapsed,
  onToggleSidebar,
  isCanvasFullscreen,
  onToggleFullscreen,
  onExportCsv,
  exportDisabled,
}) {
  const dimensionKey = queryConfig.dimensions?.[0] || 'dimension';
  const metricKey = useMemo(
    () => resolveMetricDataKey(rows, queryConfig.metrics),
    [rows, queryConfig.metrics],
  );

  const chartData = useMemo(
    () =>
      (rows || []).map((r) => ({
        ...r,
        [dimensionKey]: r[dimensionKey] ?? r.dimension ?? '—',
        [metricKey]: Number(r[metricKey] ?? r.value ?? r.count ?? 0),
      })),
    [rows, dimensionKey, metricKey],
  );

  const tableColumns = useMemo(() => {
    if (!rows?.length) return [];
    return Object.keys(rows[0]).filter((k) => !k.startsWith('__'));
  }, [rows]);

  useLayoutEffect(() => {
    const id = window.setTimeout(notifyChartResize, 280);
    return () => window.clearTimeout(id);
  }, [isSidebarCollapsed, isCanvasFullscreen, viewMode, hasRun, rows.length]);

  const chartHeight = isCanvasFullscreen ? '100%' : 420;

  const content = (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
          flexShrink: 0,
          gap: 1,
        }}
      >
        <Typography variant="subtitle1" fontWeight={700}>
          Results
        </Typography>

        <Stack direction="row" spacing={0.5} alignItems="center">
          <ToggleButtonGroup
            size="small"
            exclusive
            value={viewMode}
            onChange={(_, v) => v && onViewModeChange(v)}
            disabled={!hasRun || loading}
            sx={{ mr: 0.5 }}
          >
            <ToggleButton value="chart" aria-label="Chart view">
              <BarChartOutlinedIcon fontSize="small" sx={{ mr: 0.5 }} />
              Chart
            </ToggleButton>
            <ToggleButton value="table" aria-label="Table view">
              <TableChartOutlinedIcon fontSize="small" sx={{ mr: 0.5 }} />
              Table
            </ToggleButton>
          </ToggleButtonGroup>

          <Tooltip title={isSidebarCollapsed ? 'Show query sidebar' : 'Hide query sidebar'}>
            <IconButton size="small" onClick={onToggleSidebar} aria-label="Toggle sidebar">
              {isSidebarCollapsed ? (
                <ChevronRightIcon fontSize="small" />
              ) : (
                <ChevronLeftIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>

          <Tooltip title={isCanvasFullscreen ? 'Exit fullscreen' : 'Fullscreen'}>
            <IconButton size="small" onClick={onToggleFullscreen} aria-label="Toggle fullscreen">
              {isCanvasFullscreen ? (
                <FullscreenExitIcon fontSize="small" />
              ) : (
                <FullscreenIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>

          <Tooltip title="Export CSV">
            <span>
              <Button
                size="small"
                variant="outlined"
                startIcon={<FileDownloadOutlinedIcon fontSize="small" />}
                onClick={onExportCsv}
                disabled={exportDisabled}
                aria-label="Export CSV"
                sx={{ minWidth: 'auto', px: 1, py: 0.25, fontSize: '0.75rem' }}
              >
                Export CSV
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
          p: isCanvasFullscreen ? 3 : 2,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {!hasRun && !loading && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              flex: 1,
              minHeight: isCanvasFullscreen ? '60vh' : 360,
              color: 'text.secondary',
              border: 2,
              borderStyle: 'dashed',
              borderColor: 'divider',
              borderRadius: 2,
              bgcolor: 'background.paper',
            }}
          >
            <BarChartOutlinedIcon sx={{ fontSize: 48, mb: 1, opacity: 0.4 }} />
            <Typography variant="subtitle1" fontWeight={600}>
              No query run yet
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 0.5, textAlign: 'center', maxWidth: 360 }}
            >
              Configure your parameters in the sidebar and click <strong>Run query</strong> to
              visualize tenant data.
            </Typography>
          </Box>
        )}

        {loading && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flex: 1,
              minHeight: isCanvasFullscreen ? '60vh' : 360,
              bgcolor: 'background.paper',
              borderRadius: 2,
            }}
          >
            <Typography color="text.secondary">Running report…</Typography>
          </Box>
        )}

        {hasRun && !loading && rows.length === 0 && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flex: 1,
              minHeight: isCanvasFullscreen ? '60vh' : 360,
              bgcolor: 'background.paper',
              borderRadius: 2,
            }}
          >
            <Typography color="text.secondary">
              No rows matched your query. Try adjusting filters or the time range.
            </Typography>
          </Box>
        )}

        {hasRun && !loading && rows.length > 0 && viewMode === 'chart' && (
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              flex: isCanvasFullscreen ? 1 : undefined,
              height: chartHeight,
              minHeight: isCanvasFullscreen ? 320 : 420,
            }}
          >
            <ResponsiveContainer
              width="100%"
              height="100%"
              key={`chart-${isCanvasFullscreen}-${isSidebarCollapsed}-${rows.length}`}
            >
              {chartData.length > 12 ? (
                <LineChart data={chartData} margin={CHART_MARGIN}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey={dimensionKey}
                    tick={{ fontSize: 11 }}
                    interval={0}
                    angle={-25}
                    textAnchor="end"
                    height={70}
                  />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                  <RechartsTooltip />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey={metricKey}
                    stroke="#D85A30"
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    name={metricKey}
                  />
                </LineChart>
              ) : (
                <BarChart data={chartData} margin={CHART_MARGIN}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey={dimensionKey}
                    tick={{ fontSize: 11 }}
                    interval={0}
                    angle={chartData.length > 6 ? -25 : 0}
                    textAnchor={chartData.length > 6 ? 'end' : 'middle'}
                    height={chartData.length > 6 ? 70 : 36}
                  />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                  <RechartsTooltip />
                  <Legend />
                  <Bar
                    dataKey={metricKey}
                    fill="#1976d2"
                    radius={[4, 4, 0, 0]}
                    name={metricKey}
                  />
                </BarChart>
              )}
            </ResponsiveContainer>
          </Paper>
        )}

        {hasRun && !loading && rows.length > 0 && viewMode === 'table' && (
          <TableContainer
            component={Paper}
            variant="outlined"
            sx={{ flex: isCanvasFullscreen ? 1 : undefined }}
          >
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {tableColumns.map((col) => (
                    <TableCell key={col} sx={{ fontWeight: 700, bgcolor: 'background.paper' }}>
                      {col}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row, idx) => (
                  <TableRow key={idx} hover>
                    {tableColumns.map((col) => (
                      <TableCell key={col}>
                        {row[col] === null || row[col] === undefined ? '—' : String(row[col])}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {hasRun && meta && (
        <Box
          sx={{
            px: 2,
            py: 1,
            borderTop: 1,
            borderColor: 'divider',
            bgcolor: 'background.paper',
            flexShrink: 0,
          }}
        >
          <Typography variant="caption" color="text.secondary" component="div">
            {meta.branch && <span>Branch: {meta.branch} · </span>}
            {meta.documentsScanned !== undefined && (
              <span>Documents scanned: {meta.documentsScanned} · </span>
            )}
            {meta.rowCount !== undefined && <span>Rows returned: {meta.rowCount} · </span>}
            {meta.truncated && <span>Truncated at limit · </span>}
            {meta.collectionName && <span>Source: {meta.collectionName}</span>}
          </Typography>
        </Box>
      )}
    </>
  );

  if (isCanvasFullscreen) {
    return (
      <Box
        sx={{
          position: 'fixed',
          inset: 0,
          zIndex: (theme) => theme.zIndex.modal + 2,
          bgcolor: 'background.paper',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {content}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        bgcolor: 'background.default',
        transition: 'flex 0.25s ease',
      }}
    >
      {content}
    </Box>
  );
}
